import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Clock, CheckCircle, AlertCircle, ExternalLink, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ProjectInvoice } from '@/hooks/services/use-project-invoices';

interface PurchasedServiceCardProps {
  invoice: ProjectInvoice;
  onViewInvoice?: (url: string) => void;
  onDownloadInvoice?: (url: string) => void;
}

export const PurchasedServiceCard: React.FC<PurchasedServiceCardProps> = ({
  invoice,
  onViewInvoice,
  onDownloadInvoice,
}) => {
  const formatPrice = (price: number, currency: string = 'AUD') => {
    const formatter = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return formatter.format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle className="h-3 w-3 mr-1" />
            Paid
          </Badge>
        );
      case 'sent':
      case 'open':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            <Clock className="h-3 w-3 mr-1" />
            Pending Payment
          </Badge>
        );
      case 'draft':
        return (
          <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400">
            <FileText className="h-3 w-3 mr-1" />
            Draft
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
            <AlertCircle className="h-3 w-3 mr-1" />
            Payment Failed
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            {status}
          </Badge>
        );
    }
  };

  const getInvoiceTypeLabel = (type: string) => {
    switch (type) {
      case 'deposit':
        return 'Deposit Invoice (50%)';
      case 'final':
        return 'Final Invoice (50%)';
      default:
        return 'Service Invoice';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "p-4 rounded-lg",
        "bg-white dark:bg-gray-800",
        "border border-gray-200 dark:border-gray-700",
        "hover:shadow-md transition-shadow"
      )}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100">
              {invoice.invoice_number}
            </h4>
            {getStatusBadge(invoice.status)}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {getInvoiceTypeLabel(invoice.invoice_type)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {formatPrice(invoice.amount_total, invoice.currency)}
          </p>
          {invoice.amount_paid > 0 && invoice.amount_paid < invoice.amount_total && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Paid: {formatPrice(invoice.amount_paid, invoice.currency)}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
        <span>Created: {formatDate(invoice.created_at)}</span>
        {invoice.paid_at && (
          <span>Paid: {formatDate(invoice.paid_at)}</span>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        {invoice.hosted_invoice_url && (
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={() => onViewInvoice?.(invoice.hosted_invoice_url!)}
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            View
          </Button>
        )}
        {invoice.invoice_pdf && (
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={() => onDownloadInvoice?.(invoice.invoice_pdf!)}
          >
            <Download className="h-3 w-3 mr-1" />
            PDF
          </Button>
        )}
        {invoice.status === 'sent' && invoice.hosted_invoice_url && (
          <Button
            size="sm"
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => window.open(invoice.hosted_invoice_url!, '_blank')}
          >
            Pay Now
          </Button>
        )}
      </div>
    </motion.div>
  );
};